import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface TutorialOverlayProps {
  targetSelector: string;
  title: string;
  message: string;
  position?: "top" | "bottom" | "left" | "right";
  onNext?: () => void;
  onSkip?: () => void;
  isVisible: boolean;
  currentStep?: number;
  totalSteps?: number;
}

export function TutorialOverlay({
  targetSelector,
  title,
  message,
  position = "bottom",
  onNext,
  onSkip,
  isVisible,
  currentStep = 0,
  totalSteps = 1,
}: TutorialOverlayProps) {
  const [overlayPosition, setOverlayPosition] = useState({ top: 0, left: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);

  // Positions where the tooltip will appear relative to the target element
  const positionMap = {
    top: { y: -10, align: "items-end" },
    bottom: { y: 10, align: "items-start" },
    left: { x: -10, align: "justify-end" },
    right: { x: 10, align: "justify-start" },
  };

  const updatePosition = (element: Element) => {
    if (!overlayRef.current) return;

    const targetRect = element.getBoundingClientRect();
    const overlayRect = overlayRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    // Check if we're on a mobile device by screen width
    const isMobile = window.innerWidth < 640; // 640px is the 'sm' breakpoint in Tailwind

    // Center position is a special case for mobile where we center the tooltip regardless of target
    const useCenterPosition =
      isMobile && (position === "left" || position === "right");

    if (useCenterPosition) {
      // On mobile, for left/right positions, we'll center the tooltip instead
      top = window.innerHeight / 2 - overlayRect.height / 2;
      left = window.innerWidth / 2 - overlayRect.width / 2;
    } else {
      // Standard positioning logic for desktop or top/bottom on mobile
      switch (position) {
        case "top":
          top = targetRect.top - overlayRect.height - 15;
          left = targetRect.left + targetRect.width / 2 - overlayRect.width / 2;
          break;
        case "bottom":
          top = targetRect.bottom + 15;
          left = targetRect.left + targetRect.width / 2 - overlayRect.width / 2;
          break;
        case "left":
          top = targetRect.top + targetRect.height / 2 - overlayRect.height / 2;
          left = targetRect.left - overlayRect.width - 15;
          break;
        case "right":
          top = targetRect.top + targetRect.height / 2 - overlayRect.height / 2;
          left = targetRect.right + 15;
          break;
      }
    }

    // Mobile-specific adjustments
    if (isMobile) {
      const padding = 12; // Smaller padding for mobile

      // For mobile, ensure we have enough space to tap the buttons
      // Ensure the tutorial isn't too close to the bottom (leave room for safe area)
      const safeAreaBottom = 70; // Space for gesture area and safe area on mobile

      if (top + overlayRect.height > window.innerHeight - safeAreaBottom) {
        top = window.innerHeight - overlayRect.height - safeAreaBottom;
      }
    } else {
      // Desktop positioning bounds
      const padding = 16;

      // Handle vertical positioning
      if (top < padding) {
        // If tooltip would be cut off at the top and we're using 'top' position,
        // switch to bottom positioning
        if (position === "top") {
          top = targetRect.bottom + 15;
        } else {
          top = padding;
        }
      } else if (top + overlayRect.height > window.innerHeight - padding) {
        // If tooltip would be cut off at the bottom and we're using 'bottom' position,
        // switch to top positioning
        if (position === "bottom") {
          top = targetRect.top - overlayRect.height - 15;
        } else {
          top = window.innerHeight - overlayRect.height - padding;
        }
      }

      // Handle horizontal positioning
      if (left < padding) {
        left = padding;
      } else if (left + overlayRect.width > window.innerWidth - padding) {
        left = window.innerWidth - overlayRect.width - padding;
      }
    }

    // Common bounds checking for both mobile and desktop
    // Ensure minimum padding from edges
    const minPadding = isMobile ? 10 : 16;

    // Make sure tooltip stays within viewport
    if (top < minPadding) top = minPadding;
    if (left < minPadding) left = minPadding;
    if (top + overlayRect.height > window.innerHeight - minPadding) {
      top = window.innerHeight - overlayRect.height - minPadding;
    }
    if (left + overlayRect.width > window.innerWidth - minPadding) {
      left = window.innerWidth - overlayRect.width - minPadding;
    }

    setOverlayPosition({ top, left });
  };

  useEffect(() => {
    if (!isVisible) return;

    console.log("Looking for element with selector:", targetSelector);
    let targetElement = document.querySelector(targetSelector);
    let findElementAttempts = 0;

    // Function to find the target element with retries
    const findTargetElement = () => {
      const foundElement = document.querySelector(targetSelector);
      if (foundElement) {
        console.log("Found target element directly:", targetSelector);
        return foundElement;
      }

      findElementAttempts++;
      console.warn(
        `Tutorial target element not found (attempt ${findElementAttempts}): ${targetSelector}`,
      );

      // Log out all available elements with classes for debugging
      const allElements = document.querySelectorAll("*");
      const classesFound = new Set<string>();
      allElements.forEach((el) => {
        if (el.classList && el.classList.length > 0) {
          el.classList.forEach((cls) => classesFound.add(cls));
        }
      });
      console.log(
        "Available classes in DOM:",
        Array.from(classesFound).sort().join(", "),
      );

      if (findElementAttempts >= 8) {
        // Increased retry attempts
        // After max attempts, try to use a fallback element if this is a card or specific UI element
        const fallbackMap: Record<string, string[]> = {
          ".player-hand": [
            "#game-container",
            ".card-container",
            ".hand-container",
          ],
          ".player-hand .card": [".card", ".card-container", "#game-container"],
          ".game-board": [".board", ".board-container", "#game-container"],
          "#game-container": ["#root", "body"],
        };

        // Try alternative selectors based on the target
        const alternativeSelectors = fallbackMap[targetSelector] || [];
        for (const altSelector of alternativeSelectors) {
          const altElement = document.querySelector(altSelector);
          if (altElement) {
            console.log(
              `Using alternative selector ${altSelector} instead of ${targetSelector}`,
            );
            return altElement;
          }
        }

        // Final fallback to any major container
        const fallbackElement =
          document.querySelector("#game-container") ||
          document.querySelector("#root") ||
          document.querySelector("body");

        if (fallbackElement) {
          console.log(
            "Using fallback element for tutorial highlight:",
            fallbackElement.tagName,
          );
          return fallbackElement;
        }
        console.error("Failed to find any suitable element for tutorial");
        return null;
      }

      return null;
    };

    // Try to find the element immediately
    targetElement = findTargetElement();

    if (!targetElement) {
      // If element not found immediately, set up retry with delay
      const findElementInterval = setInterval(() => {
        targetElement = findTargetElement();

        if (targetElement || findElementAttempts >= 8) {
          // Increased retry attempts
          clearInterval(findElementInterval);

          if (targetElement) {
            setupHighlightAndPositioning(targetElement);
          }
        }
      }, 800); // Increased interval for more time to render

      // Clean up interval if component unmounts during search
      return () => {
        clearInterval(findElementInterval);
        document.querySelectorAll(".tutorial-highlight").forEach((el) => {
          el.classList.remove("tutorial-highlight");
        });
      };
    } else {
      // Element found immediately, set up highlight and positioning
      return setupHighlightAndPositioning(targetElement);
    }

    // Function to set up highlighting and positioning for a found element
    function setupHighlightAndPositioning(element: Element) {
      console.log("Found target element:", element);

      // If the element was already highlighted by a previous step, remove that first
      document.querySelectorAll(".tutorial-highlight").forEach((el) => {
        if (el !== element) {
          el.classList.remove("tutorial-highlight");
        }
      });

      // Add highlight class to the target element
      element.classList.add("tutorial-highlight");

      // Initial position update
      updatePosition(element);

      // Update position on resize or scroll
      const handleResize = () => {
        // We check if the element is still in the DOM each time
        const el = document.querySelector(targetSelector);
        if (el) {
          updatePosition(el);
        } else {
          console.warn(`Target element disappeared: ${targetSelector}`);
          // Try to find it again
          const newElement = findTargetElement();
          if (newElement) {
            updatePosition(newElement);
          }
        }
      };

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleResize);

      // Refresh position every 300ms to handle any dynamic content changes
      const interval = setInterval(handleResize, 300);

      return () => {
        if (element) {
          element.classList.remove("tutorial-highlight");
        }

        // Clean up all potential highlighted elements when component unmounts
        document.querySelectorAll(".tutorial-highlight").forEach((el) => {
          el.classList.remove("tutorial-highlight");
        });

        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleResize);
        clearInterval(interval);
      };
    }
  }, [isVisible, targetSelector, position]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Semi-transparent overlay to focus attention - don't block interaction with the tutorial */}
          <motion.div
            className="fixed inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Tooltip popup with directional indicator */}
          <motion.div
            ref={overlayRef}
            className={cn(
              "fixed z-[10000] w-[90vw] max-w-xs sm:w-auto bg-black/90 border border-primary/30 p-4 sm:p-5 rounded-lg",
              "shadow-lg shadow-primary/20 text-white pointer-events-auto backdrop-blur-sm",
              "before:absolute before:w-4 before:h-4 before:bg-black/90 before:border-t before:border-l before:border-primary/30 before:transform",
              "md:min-w-[250px]", // Minimum width on medium screens and up
              "touch-auto", // Better touch behavior
              position === "bottom" &&
                "before:top-[-8px] before:-translate-x-1/2 before:left-1/2 before:rotate-45",
              position === "top" &&
                "before:bottom-[-8px] before:-translate-x-1/2 before:left-1/2 before:rotate-[225deg]",
              position === "left" &&
                "before:right-[-8px] before:top-1/2 before:-translate-y-1/2 before:rotate-[135deg] sm:before:block before:hidden",
              position === "right" &&
                "before:left-[-8px] before:top-1/2 before:-translate-y-1/2 before:rotate-[-45deg] sm:before:block before:hidden",
            )}
            style={{
              top: overlayPosition.top,
              left: overlayPosition.left,
            }}
            initial={{ opacity: 0, scale: 0.9, ...positionMap[position] }}
            animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* Title with optional step indicator */}
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-semibold text-primary drop-shadow-glow leading-tight">
                  {title}
                </h3>
                {totalSteps > 1 && (
                  <span className="text-xs text-white/70 ml-2">
                    {currentStep + 1}/{totalSteps}
                  </span>
                )}
              </div>

              <p className="text-sm leading-relaxed">{message}</p>

              {/* Progress bar for tutorial steps */}
              {totalSteps > 1 && (
                <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 rounded-full"
                    style={{
                      width: `${((currentStep + 1) / totalSteps) * 100}%`,
                    }}
                  />
                </div>
              )}

              <div className="flex justify-between mt-2 sm:mt-3 w-full">
                {onSkip && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onSkip) onSkip();
                    }}
                    className="text-xs text-white border-white/30 hover:bg-white/10 px-3 sm:px-4 py-1.5 sm:py-2 min-w-[70px] sm:min-w-[80px] rounded-md"
                  >
                    Skip
                  </Button>
                )}

                {onNext && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onNext) onNext();
                    }}
                    className="text-xs ml-auto bg-primary text-black hover:bg-primary/90 px-3 sm:px-4 py-1.5 sm:py-2 min-w-[60px] sm:min-w-[80px] font-medium rounded-md"
                  >
                    {currentStep === totalSteps - 1 ? "Finish" : "Next"}
                  </Button>
                )}
              </div>

              {/* Mobile swipe hint - only show on first step on mobile devices */}
              {currentStep === 0 && window.innerWidth < 640 && (
                <div className="text-[10px] text-center text-white/50 mt-1">
                  Swipe left/right to navigate
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
